import type {
  ApiSuccess,
  ListNotificationsQuery,
  NotificationItem,
  RegisterDeviceRequest,
  DeviceSummary,
} from '@todo/shared';
import { del, get, getPaged, patch, post } from './client';

/**
 * Typed wrappers around /api/notifications and /api/devices. Components
 * import these, never `api` directly, so a URL change happens in one place.
 */
export const notificationApi = {
  /** DELETE /api/devices/:installationId - logout releases this browser's
   *  push token so a signed-out machine goes quiet. */
  unregisterDevice(installationId: string): Promise<void> {
    return del(`/devices/${installationId}`);
  },

  /** GET /api/notifications - paginated, newest first. */
  list(query: ListNotificationsQuery = {}): Promise<ApiSuccess<NotificationItem[]>> {
    return getPaged<NotificationItem[]>(
      '/notifications',
      query as Record<string, unknown>,
    );
  },

  /** GET /api/notifications/unread-count - cheap, made for polling. */
  async unreadCount(): Promise<number> {
    const { count } = await get<{ count: number }>('/notifications/unread-count');
    return count;
  },

  /** PATCH /api/notifications/read */
  async markRead(ids: string[]): Promise<number> {
    const { updated } = await patch<{ updated: number }>('/notifications/read', { ids });
    return updated;
  },

  /** PATCH /api/notifications/read-all */
  async markAllRead(): Promise<number> {
    const { updated } = await patch<{ updated: number }>('/notifications/read-all');
    return updated;
  },

  /**
   * POST /api/devices - unused by the web portal today; here so a web-push
   * worker (or an embedded mobile view) can register the moment it exists.
   */
  registerDevice(body: RegisterDeviceRequest): Promise<DeviceSummary> {
    return post<DeviceSummary>('/devices', body);
  },
};
